import { QueryClient } from "@tanstack/react-query";
import { apiRequest } from "./api";

// Build a URL from TanStack Query key parts.
// ["/api/athletes/me"]                   → "/api/athletes/me"
// ["/api/athletes", "uuid", "journal"]   → "/api/athletes/uuid/journal"
async function defaultQueryFn({ queryKey }: { queryKey: readonly unknown[] }) {
  const parts = (queryKey as (string | number | null | undefined)[]).filter(Boolean);
  const path = parts.join("/").replace(/\/+/g, "/");

  const res = await apiRequest(path);
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${path} ${text}`.trim());
  }
  return res.json();
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
      queryFn: defaultQueryFn,
    },
  },
});

// Typed return can be your User type; using unknown for now.
export async function fetchCurrentUser(): Promise<unknown | null | "unavailable"> {
  try {
    const res = await apiRequest("/user");
    if (res.status === 401 || res.status === 403) return null; // unauthenticated
    if (res.status === 404) return "unavailable"; // endpoint missing — don't redirect
    if (!res.ok) {
      // 5xx: surface error but DO NOT treat as logged out; caller decides
      const text = await res.text().catch(() => "");
      const err: any = new Error(`API ${res.status}: /user ${text}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  } catch (e) {
    // Network or CORS error — don't redirect
    const err: any = e instanceof Error ? e : new Error("Network error");
    err.kind = "network";
    throw err;
  }
}

// Helpful default for consumers that query "/api/user" by key:
queryClient.setQueryDefaults(["/api/user"], {
  queryFn: fetchCurrentUser,
  refetchOnReconnect: false,
  refetchInterval: false,
  retry: false,
});
